import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Drawer,
  IconButton,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import { DataGridPro, GridActionsCellItem, koKR } from '@mui/x-data-grid-pro';
import {
  Add,
  Close,
  Delete,
  Edit,
  Remove,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmDialog } from '../dialog';
import { CustomNoRowsOverlay } from '../datagrid';
import { LEAD_LABELS } from '../GTestComparison/leadFieldUtils';
import * as gtestComparisonActions from '../../store/gtestComparison';

const emptyForm = {
  item: '',
  unit: '',
  criterion: '',
  result: '합격',
  method: '',
  leadField: '',
  criterionFormula: '',
};

const officeFields = (list) => {
  const names = Array.isArray(list) ? list.map((name) => String(name ?? '')).filter((name) => name.trim()) : [];
  const rows = names.length ? names : [''];
  return rows.map((name) => ({ key: uuidv4(), name }));
};

const leadHelper = Object.entries(LEAD_LABELS)
  .map(([key, label]) => `${key} (${label})`)
  .join(', ');

/** 표준에 속한 시험항목. 저장 키는 표준 id다. */
const GStandardTestItems = ({ gstandardId, readOnly }) => {
  const dispatch = useDispatch();
  const rows = useSelector((state) => state.gtestComparison.gtestComparisons);
  const [loaded, setLoaded] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(() => ({ ...emptyForm, officeItems: officeFields([]) }));
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const reload = async () => {
    if (!gstandardId) return;
    setLoaded(false);
    try {
      await dispatch(gtestComparisonActions.selectAllByGstandardId(gstandardId));
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    setDrawerOpen(false);
  }, [gstandardId]);

  useEffect(() => {
    if (!drawerOpen || !gstandardId) return undefined;
    reload();
    return () => {
      dispatch(gtestComparisonActions.initGTestComparisions());
    };
  }, [drawerOpen, gstandardId]);

  const openCreate = () => {
    setEditingId('');
    setForm({ ...emptyForm, officeItems: officeFields([]) });
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      item: row.item || '',
      unit: row.unit || '',
      criterion: row.criterion || '',
      result: row.result || '합격',
      method: row.method || '',
      leadField: row.leadField || '',
      officeItems: officeFields(row.officeItems),
      criterionFormula: row.criterionFormula || '',
    });
    setEditorOpen(true);
  };

  const updateOfficeItem = (key, name) => {
    setForm((prev) => ({
      ...prev,
      officeItems: prev.officeItems.map((row) => (row.key === key ? { ...row, name } : row)),
    }));
  };

  const addOfficeItem = () => {
    setForm((prev) => ({
      ...prev,
      officeItems: [...prev.officeItems, { key: uuidv4(), name: '' }],
    }));
  };

  const removeOfficeItem = (key) => {
    setForm((prev) => ({
      ...prev,
      officeItems: prev.officeItems.filter((row) => row.key !== key),
    }));
  };

  const handleSave = async () => {
    if (!form.item.trim()) return;
    setSaving(true);
    const body = {
      id: editingId || uuidv4(),
      gstandardId,
      item: form.item.trim(),
      unit: form.unit,
      criterion: form.criterion,
      result: form.result,
      method: form.method,
      leadField: form.leadField.trim(),
      officeItems: form.officeItems.map((row) => row.name.trim()).filter(Boolean),
      criterionFormula: form.criterionFormula,
    };
    try {
      if (editingId) await dispatch(gtestComparisonActions.modifyByStandard(body));
      else await dispatch(gtestComparisonActions.createByStandard(body));
      setEditorOpen(false);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    await dispatch(gtestComparisonActions.remove(id));
    await reload();
  };

  const handleRowOrderChange = async ({ oldIndex, targetIndex }) => {
    if (readOnly || oldIndex === targetIndex) return;
    const previous = rows || [];
    const next = [...previous];
    const [moved] = next.splice(oldIndex, 1);
    if (!moved) return;
    next.splice(targetIndex, 0, moved);
    dispatch(gtestComparisonActions.replaceList(next));
    try {
      await gtestComparisonActions.reorder({
        type: 'to',
        id: moved.id,
        gstandardId,
        toOrder: targetIndex + 1,
      });
    } catch (error) {
      dispatch(gtestComparisonActions.replaceList(previous));
    }
  };

  const setField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const columns = [
    { field: 'item', headerName: '구분', width: 140 },
    { field: 'unit', headerName: '단위', width: 90 },
    { field: 'criterion', headerName: '기준', width: 160 },
    { field: 'leadField', headerName: '선행 필드', width: 140 },
    {
      field: 'officeItems',
      headerName: '시험기관 항목명',
      width: 160,
      valueGetter: (params) => (Array.isArray(params.row?.officeItems) ? params.row.officeItems.join(', ') : ''),
    },
    { field: 'criterionFormula', headerName: '판정 수식', width: 160 },
    { field: 'result', headerName: '판정', width: 90, align: 'center', headerAlign: 'center' },
    { field: 'method', headerName: '시험방법', width: 120 },
    {
      field: 'actions',
      headerName: '기능',
      type: 'actions',
      width: 90,
      getActions: (params) => {
        const actions = [];
        if (!readOnly) {
          actions.push(
            <GridActionsCellItem icon={<Edit />} label="수정" onClick={() => openEdit(params.row)} />,
            <GridActionsCellItem icon={<Delete />} label="삭제" onClick={() => setRemoveTarget(params.row)} />,
          );
        }
        return actions;
      },
    },
  ];

  if (!gstandardId) return null;

  return (
    <>
      <Button variant="contained" sx={{ mr: 'auto' }} onClick={() => setDrawerOpen(true)}>시험항목</Button>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        PaperProps={{ sx: { width: 1100, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ flex: 1 }}>시험항목</Typography>
          {!readOnly && (
            <Button size="small" startIcon={<Add />} onClick={openCreate}>항목추가</Button>
          )}
          <IconButton aria-label="닫기" onClick={() => setDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, p: 2 }}>
          <DataGridPro
            localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows || []}
            columns={columns}
            loading={!loaded}
            hideFooter
            disableRowSelectionOnClick
            headerHeight={38}
            rowHeight={34}
            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
            onRowDoubleClick={(params) => { if (!readOnly) openEdit(params.row); }}
            rowReordering={!readOnly}
            onRowOrderChange={handleRowOrderChange}
            pinnedColumns={{ right: ['actions'] }}
            sx={{ height: '100%', fontSize: '0.85em' }}
          />
        </Box>
      </Drawer>
      <Dialog
        open={editorOpen}
        onClose={saving ? undefined : () => setEditorOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField size="small" label="구분" value={form.item} onChange={setField('item')} required />
            <TextField size="small" label="단위" value={form.unit} onChange={setField('unit')} />
            <TextField size="small" label="기준" value={form.criterion} onChange={setField('criterion')} multiline minRows={2} />
            <TextField
              size="small"
              label="선행 필드"
              value={form.leadField}
              onChange={setField('leadField')}
              helperText={leadHelper}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                시험기관 항목명
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {form.officeItems.map((row, index) => (
                  <Box key={row.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.name}
                      onChange={(e) => updateOfficeItem(row.key, e.target.value)}
                    />
                    <IconButton
                      size="small"
                      color={index === 0 ? 'primary' : 'default'}
                      aria-label={index === 0 ? '항목 추가' : '항목 삭제'}
                      onClick={index === 0 ? addOfficeItem : () => removeOfficeItem(row.key)}
                    >
                      {index === 0 ? <Add /> : <Remove />}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
            <TextField
              size="small"
              label="판정 수식"
              value={form.criterionFormula}
              onChange={setField('criterionFormula')}
              multiline
              minRows={2}
            />
            <TextField size="small" label="판정" value={form.result} onChange={setField('result')} select>
              <MenuItem value="합격">합격</MenuItem>
              <MenuItem value="불합격">불합격</MenuItem>
              <MenuItem value="참조값">참조값</MenuItem>
            </TextField>
            <TextField size="small" label="시험방법" value={form.method} onChange={setField('method')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave} disabled={saving || !form.item.trim()}>저장</Button>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>닫기</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={Boolean(removeTarget)}
        setOpen={(next) => { if (!next) setRemoveTarget(null); }}
        title="삭제"
        onConfirm={() => removeTarget && handleRemove(removeTarget.id)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        {`시험항목 「${removeTarget?.item || ''}」을 삭제하시겠습니까?`}
      </ConfirmDialog>
    </>
  );
};

export default GStandardTestItems;
